import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);

    // Keep this form's bundle select in step with the bundle chosen in the source form.
    const sourceId = this.getAttribute('bundle-source');
    const source = sourceId ? document.getElementById(sourceId) : null;
    if (source) {
      source.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.name !== 'quantity' || !input.checked) return;

        const select = this.querySelector('select[name="quantity"]');
        if (select instanceof HTMLSelectElement && Array.from(select.options).some((o) => o.value === input.value)) {
          select.value = input.value;
        }
      }, opts);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    const bundle = this.#selectedBundle(form);
    const lines = [line];
    for (const merchandiseId of bundle.giftIds) {
      // A free bottle of the same product joins the main line instead of adding a duplicate line.
      const existing = lines.find((entry) => entry.merchandiseId === merchandiseId && !entry.sellingPlanId && !entry.attributes);
      if (existing) {
        existing.quantity += 1;
      } else {
        lines.push({ merchandiseId, quantity: 1 });
      }
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines },
        {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        }
      );

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#showError(errorMessage);
      } else {
        if (bundle.discountCode) {
          // The bundle's code is what makes its gifts free; a failed code never blocks the add.
          try {
            await window.Shopify.actions.updateCart(
              { discountCodes: [bundle.discountCode] },
              { event: { context: 'cart' }, signal }
            );
          } catch (discountError) {
            if (signal.aborted) return;
          }
          this.#throwIfAborted(signal);
        }
        if (this.dataset.checkoutUrl) {
          // Buy now: the cart is updated, so go straight to checkout instead of opening the cart.
          window.location.assign(this.dataset.checkoutUrl);
          return;
        }
        await window.Shopify.actions.openCart?.();
      }
    } catch (error) {
      if (signal.aborted) return;

      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  /**
   * The chosen bundle option's free gifts and discount code. A form whose
   * quantity control carries no bundle data (the sticky bar's select) reads it
   * from the matching option in the form named by its `bundle-source` attribute.
   */
  #selectedBundle(form) {
    const empty = { giftIds: [], discountCode: '' };
    const field = form.elements.namedItem('quantity');
    let source = null;

    if (field instanceof HTMLSelectElement) {
      source = field.selectedOptions[0] || null;
    } else if (field instanceof RadioNodeList) {
      source = Array.from(field).find((input) => input.checked) || null;
    } else if (field instanceof HTMLInputElement && (field.type !== 'radio' || field.checked)) {
      source = field;
    }
    if (!source) return empty;

    const sourceId = this.getAttribute('bundle-source');
    if (!('giftIds' in source.dataset) && !('discountCode' in source.dataset) && sourceId) {
      const match = Array.from(document.getElementById(sourceId)?.querySelectorAll('input[name="quantity"]') || [])
        .find((input) => input.value === source.value);
      if (match) source = match;
    }

    const giftIds = String(source.dataset.giftIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    return { giftIds, discountCode: String(source.dataset.discountCode || '').trim() };
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
